import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * PageHeader – consistent hero block on each sub-page of the redesigned
 * multi-page site. Generous breathing space, gentle sage-green hue,
 * breadcrumb-style trail and a soft mint-rounded bottom corner.
 */
export const PageHeader = ({
    eyebrow,
    title,
    titleAccent,
    subtitle,
    breadcrumb = [],
    bg = "mint",
    testId,
    children,
}) => {
    const bgClass = {
        mint:       "bg-mint-100/70",
        sand:       "bg-sand-100/70",
        terracotta: "bg-terracotta-50",
        linen:      "bg-linen",
    }[bg] || "bg-mint-100/70";

    return (
        <section
            className={`${bgClass} pt-16 pb-20 sm:pt-24 sm:pb-28 rounded-b-[2.5rem] sm:rounded-b-[3.5rem] mb-16 sm:mb-20`}
            data-testid={testId || "page-header"}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                {breadcrumb.length > 0 && (
                    <nav className="flex items-center flex-wrap gap-1 text-sm text-sage-500/70 mb-8 reveal-up" aria-label="Breadcrumb">
                        {breadcrumb.map((b, i) => (
                            <span key={i} className="inline-flex items-center gap-1">
                                {b.to ? (
                                    <Link to={b.to} className="hover:text-sage-500 transition-colors font-body">
                                        {b.label}
                                    </Link>
                                ) : (
                                    <span className="font-body">{b.label}</span>
                                )}
                                {i < breadcrumb.length - 1 && (
                                    <ChevronRight size={14} className="text-sage-500/40" />
                                )}
                            </span>
                        ))}
                    </nav>
                )}
                {eyebrow && (
                    <div className="inline-flex items-center gap-2 mb-6 reveal-up" style={{ animationDelay: "60ms" }}>
                        <span className="inline-block w-8 h-px bg-terracotta-300" />
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {eyebrow}
                        </span>
                    </div>
                )}
                <h1 className="font-display font-black tracking-tight text-sage-500 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-4xl reveal-up" style={{ animationDelay: "130ms" }}>
                    {title}
                    {titleAccent && (
                        <span className="text-terracotta-400"> {titleAccent}</span>
                    )}
                </h1>
                {subtitle && (
                    <p className="mt-6 font-body text-lg text-sage-500/80 max-w-2xl leading-relaxed reveal-up" style={{ animationDelay: "220ms" }}>
                        {subtitle}
                    </p>
                )}
                {children && <div className="mt-8 reveal-up" style={{ animationDelay: "300ms" }}>{children}</div>}
            </div>
        </section>
    );
};
